import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ThumbsUp, ThumbsDown, X, Send } from 'lucide-react';

/**
 * PilotFeedbackWidget
 *
 * Compact thumbs-up / thumbs-down + optional note capture for pilot users.
 * Posts to `/api/pilot-feedback` with a stable `feature` tag so aggregates
 * can be computed per surface in the admin feedback overview.
 *
 * Props:
 *   feature      — required string; the tag identifying the UI surface
 *                  (e.g. 'sleepover-finance-tile', 'sleepover-adjustment-modal').
 *   context      — optional dict attached to the record (e.g. {shift_id}).
 *   cohort       — optional short string tagging the rollout wave
 *                  (e.g. 'pilot-day-1', 'prod-launch-wave-1'). Used by the
 *                  admin feedback aggregate endpoint for per-cohort slicing.
 *   compact      — when true, renders a single-line minimal variant.
 *   onSubmitted  — optional callback after successful submit.
 */
export function PilotFeedbackWidget({
  feature, context, cohort, compact = false, onSubmitted,
}) {
  const [sentiment, setSentiment] = useState(null);  // 'up' | 'down' | null
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (effectiveSentiment) => {
    if (busy || submitted) return;
    setBusy(true);
    try {
      await api.post('/pilot-feedback', {
        feature,
        sentiment: effectiveSentiment,
        note: note || null,
        context: context || null,
        cohort: cohort || null,
      });
      setSubmitted(true);
      toast.success('Feedback recorded — thanks!');
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send feedback');
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div
        className="flex items-center gap-1.5 text-[10px] text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1"
        data-testid={`pilot-feedback-done-${feature}`}
      >
        <Badge variant="outline" className="bg-green-100 text-green-700 text-[9px]">PILOT</Badge>
        Thanks for the feedback ✓
      </div>
    );
  }

  if (compact && !sentiment) {
    return (
      <div
        className="flex items-center gap-1.5 text-[10px] text-muted-foreground"
        data-testid={`pilot-feedback-widget-${feature}`}
      >
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[9px]">PILOT</Badge>
        <span>This useful?</span>
        <button
          type="button"
          onClick={() => submit('up')}
          disabled={busy}
          className="text-slate-500 hover:text-green-600 transition"
          data-testid={`pilot-feedback-up-${feature}`}
          aria-label="Thumbs up"
        >
          <ThumbsUp className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setSentiment('down')}
          disabled={busy}
          className="text-slate-500 hover:text-red-600 transition"
          data-testid={`pilot-feedback-down-${feature}`}
          aria-label="Thumbs down"
        >
          <ThumbsDown className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // Full mode: note entry after "down" click OR explicit note field for either sentiment
  return (
    <div
      className="mt-2 p-2 border border-slate-200 rounded-md bg-slate-50/50 space-y-1.5"
      data-testid={`pilot-feedback-widget-${feature}`}
    >
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[9px]">PILOT</Badge>
        <span>Feedback:</span>
        <button
          type="button"
          onClick={() => setSentiment('up')}
          className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded transition ${
            sentiment === 'up' ? 'bg-green-100 text-green-700 border border-green-300' : 'text-slate-500 hover:text-green-600'
          }`}
          data-testid={`pilot-feedback-up-${feature}`}
        >
          <ThumbsUp className="w-3 h-3" /> Useful
        </button>
        <button
          type="button"
          onClick={() => setSentiment('down')}
          className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded transition ${
            sentiment === 'down' ? 'bg-red-100 text-red-700 border border-red-300' : 'text-slate-500 hover:text-red-600'
          }`}
          data-testid={`pilot-feedback-down-${feature}`}
        >
          <ThumbsDown className="w-3 h-3" /> Confusing
        </button>
        {sentiment && (
          <button
            type="button"
            className="ml-auto text-slate-400 hover:text-slate-600"
            onClick={() => { setSentiment(null); setNote(''); }}
            data-testid={`pilot-feedback-reset-${feature}`}
            aria-label="Reset"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {sentiment && (
        <>
          <Textarea
            rows={2}
            placeholder="Optional: what could be better?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="text-[11px]"
            data-testid={`pilot-feedback-note-${feature}`}
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => submit(sentiment)}
              disabled={busy}
              className="h-6 text-[10px]"
              data-testid={`pilot-feedback-submit-${feature}`}
            >
              <Send className="w-3 h-3 mr-1" /> Send
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
